import React, { useEffect, useId, useRef } from "react";
import ReactDOM from "react-dom";
import Icon from "./Icon";

/**
 * The one dialog primitive. Centered card, blurred backdrop over the whole
 * viewport (header and tab bar included), Escape closes, focus is trapped,
 * body scroll locks, internal scroll when taller than the viewport, and a
 * consistent 40x40 close button inside the card.
 *
 * tone="light" is the white app modal; tone="dark" the raised dark card.
 * The backdrop stops intercepting pointer events the moment closing starts,
 * so the first click after a modal is never swallowed.
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Small uppercase line above the title. */
  eyebrow?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  tone?: "light" | "dark";
  size?: "sm" | "md" | "lg";
  /** Hide the close button (e.g. forced choices). Escape still works unless `dismissible` is false. */
  hideClose?: boolean;
  dismissible?: boolean;
  /** Stacked on top of another dialog (quiz inside roadmap). */
  layer?: 1 | 2;
  className?: string;
  ariaLabel?: string;
  /** Initial focus target; defaults to the close button or the first focusable. */
  initialFocusRef?: React.RefObject<HTMLElement>;
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

function getRoot(): HTMLElement {
  let root = document.getElementById("overlay-root");
  if (!root) { root = document.createElement("div"); root.id = "overlay-root"; document.body.appendChild(root); }
  return root;
}

let openCount = 0;

/** Taps on the backdrop are ignored this long after the dialog opens. */
export const BACKDROP_GRACE_MS = 700;

export default function Modal({
  open, onClose, title, eyebrow, children, footer, tone = "light", size = "md",
  hideClose = false, dismissible = true, layer = 1, className = "", ariaLabel, initialFocusRef,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);
  const openedAtRef = useRef(0);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    closingRef.current = false;
    openedAtRef.current = Date.now();
    openCount += 1;
    document.body.classList.add("modal-open");
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const target = initialFocusRef?.current || cardRef.current?.querySelector<HTMLElement>("[data-autofocus]") || cardRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissible) { e.stopPropagation(); close(); return; }
      if (e.key !== "Tab" || !cardRef.current) return;
      const nodes = Array.from(cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) document.body.classList.remove("modal-open");
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    onClose();
  };

  if (!open) return null;

  const node = (
    <div
      className={`mr-modal mr-modal--${tone} mr-modal--${size} mr-modal--layer-${layer} ${className}`}
      role="presentation"
    >
      <div
        className="mr-modal__backdrop"
        onPointerDown={(e) => {
          // A dialog opened by a tap (e.g. out of stars while tapping a
          // producer) must survive the next quick taps that land outside it.
          if (Date.now() - openedAtRef.current < BACKDROP_GRACE_MS) return;
          if (dismissible && e.target === e.currentTarget) close();
        }}
        style={closingRef.current ? { pointerEvents: "none" } : undefined}
      />
      <div
        ref={cardRef}
        className="mr-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
      >
        {(title || eyebrow || !hideClose) && (
          <header className="mr-modal__head">
            <div className="mr-modal__titles">
              {eyebrow ? <div className="mr-overline">{eyebrow}</div> : null}
              {title ? <h2 id={titleId} className="mr-modal__title">{title}</h2> : null}
            </div>
            {!hideClose && (
              <button type="button" className="mr-iconbtn mr-modal__close" onClick={close} aria-label="Close" data-autofocus>
                <Icon name="close" size={22} />
              </button>
            )}
          </header>
        )}
        <div className="mr-modal__body">{children}</div>
        {footer ? <footer className="mr-modal__foot">{footer}</footer> : null}
      </div>
    </div>
  );
  return ReactDOM.createPortal(node, getRoot());
}
